import { AppError } from '../errors/app-error.js';
import { nextStage, stageRank } from './membership.entity.js';
// ─── Network submissions (Camino TMAI · R1) ─────────────────────────────────
// Publication BY COPY: when a seminary submits, its latest readiness report is
// FROZEN into a document that lives ONLY in the network tenant
// (`tenants/{networkTenantId}/networkSubmissions/{id}`). One document PER ROUND:
// re-submitting after a `returned` decision creates a NEW document with
// `round + 1` chained via `previousSubmissionId` — nothing submitted is ever
// edited. The seminary keeps a lightweight status mirror
// (`tenants/{seminaryId}/membershipSubmissions/{id}`, same id) so it can see its
// own history without ever reading the network tenant.
//
// PRIVACY INVARIANTS (director, 2026-08-04 — non-negotiable):
// - The reviewer reads EXCLUSIVELY the network tenant. No cross-tenant reads
//   into the seminary — not even with the Admin SDK.
// - `assertNetworkPublishable` refuses ANY artifact carrying `containsPii`
//   (e.g. the roster annex). The packet copied here is aggregate, zero PII.
// - Doctrinal explanations are NEVER stored nor transported by the platform;
//   what TMAI requires (a response from every person) travels inside the packet
//   as DOCTRINAL_ATTESTATION_RATE — counts, no names.
export const NETWORK_SUBMISSION_STATUSES = [
    'submitted',
    'returned',
    'approved',
    'withdrawn',
    /** Una consulta que quedó reemplazada porque el seminario presentó su solicitud
     *  formal. NO es `returned`: nadie escribió observaciones, y `returned` las
     *  exige. Cerrarla con la etiqueta equivocada le diría al seminario que le
     *  devolvieron algo que en realidad él mismo superó. */
    'superseded',
    /** Rechazo definitivo. CIERRA la ronda y NO TOCA LA ESCALERA — el TC sigue en
     *  su escalón y puede volver a someter. Lo que lo separa de `returned` no es una
     *  consecuencia mecánica distinta sino el MENSAJE: `returned` dice "corregí
     *  esto"; `rejected` dice "no, y no por un detalle". Si además hay que bajar de
     *  etapa, eso es `networkRevokeSeminaryStage`: un acto separado, explícito y
     *  auditado. Bajar de etapa es gobernanza, no efecto colateral de un trámite. */
    'rejected',
];
/** Una ronda sigue viva mientras espera respuesta. */
export function isOpenStatus(status) {
    return status === 'submitted';
}
// ─── Consulta vs. solicitud ─────────────────────────────────────────────────
// El proceso de TMAI tiene DOS trámites formales —la solicitud de candidatura y
// la de membresía— en el borde de cada fase que cambia de estatus. Todo lo demás
// que el TC presenta es FEEDBACK: quiere saber cómo viene, no cambiar de escalón.
export const SUBMISSION_KINDS = [
    /** Feedback. Se RESPONDE con observaciones; no puede aprobarse. */
    'advisory',
    /** Trámite. Su aprobación mueve la escalera. */
    'formal',
    /** Anterior a este modelo. NO ES EMITIBLE — ver `EmittableKind`.
     *
     *  Las rondas que ya existían no son advisory ni formal: se presentaron cuando
     *  la distinción no existía, y CUALQUIERA de las dos etiquetas miente sobre
     *  ellas. Dos fueron aprobadas y movieron la escalera (conducta formal) con un
     *  escalón objetivo que hoy fuerza advisory. Marcarlas advisory las pondría en
     *  violación del invariante "una advisory no se aprueba" — un dato histórico
     *  rompiendo un test escrito hoy.
     *
     *  Mismo criterio que el backfill de `achievedStage`, que usó `recognized` y no
     *  `earned` porque "la plataforma no derivó ese avance por el mecanismo — el
     *  mecanismo no existía". */
    'legacy',
];
/** Qué tipos puede presentar el TC en su situación actual.
 *
 *  LA DERIVACIÓN ACOTA; LA ELECCIÓN EXISTE SOLO DONDE HAY DECISIÓN REAL. Derivar
 *  el tipo de `overallStatus` sería inferir una DECLARACIÓN a partir de una
 *  AUSENCIA — la misma clase de error que confundir `not_met` con `no_data`. El
 *  caso que lo rompe es normal: un pre-candidato que completó todo y quiere una
 *  última pasada de feedback ANTES de aplicar quedaría atrapado por su propia
 *  completitud, y un formal disparado sin querer arranca un plazo normativo
 *  IRREVERSIBLE de dos años.
 *
 *  Elegir entre "pido feedback" y "aplico" es una decisión institucional que solo
 *  el TC puede tomar. Y no se autoconcede nada: el escalón lo mueve la aprobación
 *  de TMAI igual. */
export function allowedKinds(targetStage, overallStatus) {
    const isFormalStage = targetStage === 'candidate' || targetStage === 'member';
    if (!isFormalStage)
        return ['advisory'];
    return overallStatus === 'ready' ? ['advisory', 'formal'] : ['advisory'];
}
/** Un tipo fuera del conjunto se RECHAZA. No se corrige en silencio: si el
 *  cliente pidió `formal` y no corresponde, hay que decirlo, no degradarlo a
 *  advisory sin avisar. */
export function assertKindAllowed(requested, allowed) {
    if (!allowed.includes(requested)) {
        throw AppError.validation(`Submission kind '${requested}' is not available here (allowed: ${allowed.join(', ')})`, requested === 'formal' ? 'network.formal_not_available' : 'network.kind_not_allowed');
    }
}
// ─── The publishable packet ─────────────────────────────────────────────────
// What crosses to the network is NOT the internal readiness report: it is a
// SEPARATE type built field by field from an explicit policy. Adding a field to
// `MembershipReadinessReport` BREAKS THE BUILD here until somebody decides
// `publish` or `omit` — no silent leak, no silent loss.
/** Per-field decision for everything the internal readiness report carries.
 *  `Record<keyof MembershipReadinessReport, …>` is the compile-time gate: a new
 *  source field leaves this map incomplete and the build fails. */
export const PACKET_FIELD_POLICY = {
    id: 'publish',
    generatedAt: 'publish',
    frameworkVersion: 'publish',
    stage: 'publish',
    criteria: 'publish',
    byCategory: 'publish',
    warnings: 'publish',
    overallStatus: 'publish',
    // `as const` keeps the literals (so PublishablePacket can be derived from
    // them); `satisfies` is the exhaustiveness gate — a new field on
    // MembershipReadinessReport makes THIS LINE fail to compile.
};
/** Builds the network-bound packet by WHITELIST — never by reference copy. A
 *  field that is not written here never reaches the network, whatever the
 *  source object happens to carry at runtime. */
export function buildPublishablePacket(report) {
    return {
        id: report.id,
        generatedAt: report.generatedAt,
        frameworkVersion: report.frameworkVersion,
        stage: report.stage,
        criteria: report.criteria.map((c) => ({
            criterionId: c.criterionId,
            category: c.category,
            status: c.status,
            gating: c.gating,
            ...(c.derived
                ? {
                    derived: {
                        indicator: c.derived.indicator,
                        ...(c.derived.value !== undefined ? { value: c.derived.value } : {}),
                        ...(c.derived.target !== undefined ? { target: c.derived.target } : {}),
                        satisfied: c.derived.satisfied,
                        dataAvailable: c.derived.dataAvailable,
                        ...(c.derived.detail
                            ? {
                                detail: {
                                    numerator: c.derived.detail.numerator,
                                    ...(c.derived.detail.denominator !== undefined
                                        ? { denominator: c.derived.detail.denominator }
                                        : {}),
                                },
                            }
                            : {}),
                    },
                }
                : {}),
        })),
        byCategory: report.byCategory.map((b) => ({
            category: b.category,
            met: b.met,
            total: b.total,
            // Cuántos criterios de la categoría NADIE respondió. Viaja a la red porque
            // es justamente lo que evita que el revisor lea "0 de 5" como "incumple 5".
            noData: b.noData,
        })),
        warnings: report.warnings.map((w) => ({
            code: w.code,
            ...(w.criterionId !== undefined ? { criterionId: w.criterionId } : {}),
        })),
        overallStatus: report.overallStatus,
        containsPii: false,
    };
}
/** The frozen copy living in the NETWORK tenant. Immutable after submission
 *  except for appending the decision (or the seminary withdrawing an open one). */
/** DE QUÉ ES la ronda. Nació con un solo asunto —la membresía institucional— y
 *  por eso el asunto era implícito. Al abrirse CAP Fase 2 la misma maquinaria
 *  transporta un segundo asunto, y un asunto implícito con dos valores posibles
 *  es una ambigüedad que se resuelve adivinando en el punto de lectura. */
export const NETWORK_SUBMISSION_SUBJECTS = ['membership', 'capProposal'];
/** Deterministic round id — THE mutual-exclusion mechanism (R1-PR2).
 *
 *  The uniqueness scope of an OPEN round is the SEMINARY (not seminary+stage): a
 *  seminary pursues one target stage at a time, so the id must encode exactly
 *  that scope. Two concurrent submits derive the same `round` and therefore the
 *  SAME id, so the storage `create` precondition lets exactly one through — a
 *  query cannot lock a document that does not exist yet, so it can never provide
 *  this guarantee. `stage` travels as a FIELD (the UI reads it from there).
 *
 *  A stale round derivation is harmless: it collides with an existing id and the
 *  create fails closed. It never duplicates. */
export function buildSubmissionId(seminaryTenantId, round) {
    return `${seminaryTenantId}_r${round}`;
}
/** Id determinista de una ronda de PROPUESTA CAP — mismo mecanismo, OTRO
 *  ALCANCE, y la diferencia no es un detalle de nombres.
 *
 *  En membresía el alcance de una ronda abierta es EL SEMINARIO: un centro
 *  persigue un escalón por vez, así que dos rondas abiertas del mismo centro son
 *  siempre un error y el id lo impide por construcción.
 *
 *  En CAP eso sería FALSO. Un centro puede tener varios programas en revisión al
 *  mismo tiempo —son certificaciones distintas, de programas distintos, que no
 *  compiten entre sí— y bloquear la segunda porque la primera está abierta
 *  inventaría una regla que TMAI no tiene. El alcance real es
 *  PROGRAMA + CAP + NIVEL, que es exactamente el alcance de la certificación que
 *  la ronda persigue.
 *
 *  Reusar `buildSubmissionId` acá habría hecho colisionar dos programas del mismo
 *  centro en el mismo id: la segunda sometida fallaría al crear, y el mensaje
 *  hablaría de una ronda abierta que el usuario no reconocería porque sería de
 *  OTRO programa. Un bug que se ve como un permiso. */
export function buildCapSubmissionId(seminaryTenantId, programId, capCode, level, round) {
    return `${seminaryTenantId}__${programId}__${capCode}__${level}_r${round}`;
}
/** Product decision (director, 2026-08-04, DECISIONS): a packet with open gaps
 *  is NOT submitted. The TMAI reviewer is not the detector of our own
 *  incompleteness. Hard block, before anything is copied. */
/** No se saltean escalones. Se sube de a uno, siempre.
 *
 *  Se valida DOS veces y no es redundancia: al someter (barato, evita que un
 *  revisor lea 41 criterios de un escalón imposible) y al APROBAR. Entre un
 *  submit y su aprobación pueden pasar semanas, y en el medio la red pudo bajar
 *  de escalón al seminario — la validación al someter es UX, la de aprobar es la
 *  que manda.
 *
 *  El reconocimiento NO pasa por acá a propósito: reconocer un escalón ganado
 *  fuera de la plataforma es justamente saltar. */
export function assertSequentialAdvance(achieved, claimed) {
    const expected = nextStage(achieved);
    if (expected === null) {
        throw AppError.validation('Your institution has reached the highest stage. There is no next stage to apply for.', 'network.max_stage_reached');
    }
    if (claimed !== expected) {
        throw AppError.validation('You advance one stage at a time. From your current stage, apply for the next one.', 'network.one_stage_at_a_time');
    }
}
/** Una baja de etapa va HACIA ABAJO. Ni igual ni superior, aunque el parámetro
 *  venga mal armado — bajar a la misma etapa no es una baja, y "bajar" hacia
 *  arriba sería un ascenso sin evidencia disfrazado de sanción. */
export function assertStrictlyLower(achieved, target) {
    if (stageRank(target) >= stageRank(achieved)) {
        throw AppError.validation('A stage reduction must go to a stage below the current one.', 'network.demotion_must_be_lower');
    }
}
/** `reason` obligatorio y no vacío. Mismo trato que `observations` al devolver
 *  una submission: quitar o conceder un estatus sin decir por qué no le sirve a
 *  nadie, y en una baja es lo único que le dice al seminario qué recuperar. */
export function assertStageReason(reason, act) {
    if (!reason || reason.trim().length === 0) {
        // DOS códigos en vez de un parámetro `act` con la frase ya armada. Un
        // parámetro que transporta texto YA TRADUCIDO derrota el propósito: el
        // idioma volvería a decidirse acá, en una capa que no tiene i18n.
        throw AppError.validation('Stage change requires a reason', act === 'revoke' ? 'network.revoke_reason_required' : 'network.recognize_reason_required');
    }
}
/** Construye la entrada de historia, validando lo que cada `kind` exige. Único
 *  constructor: nadie arma un StageChange a mano. */
export function buildStageChange(input) {
    if (input.kind === 'earned' && !input.submissionId) {
        throw AppError.validation('An earned advance must reference the submission that produced it', 'network.earned_requires_submission');
    }
    if (input.kind !== 'earned') {
        assertStageReason(input.reason, input.kind === 'revoked' ? 'revoke' : 'recognize');
    }
    return {
        stage: input.stage,
        previousStage: input.previousStage,
        kind: input.kind,
        at: input.at,
        byUid: input.byUid,
        ...(input.reason ? { reason: input.reason } : {}),
        ...(input.submissionId ? { submissionId: input.submissionId } : {}),
    };
}
/** No se presenta una SOLICITUD con brechas abiertas.
 *
 *  C4 no se revierte: se PRECISA. La regla siempre fue sobre el trámite formal —
 *  pedirle a TMAI que te acepte mientras te faltan requisitos gating. Someter con
 *  brechas es exactamente el SENTIDO de una consulta: "esto me falta, ¿voy bien?".
 *  Por eso dejó de ser un guard sobre todo submit y pasó a ser el clasificador de
 *  `allowedKinds`, que ya devuelve solo `advisory` cuando hay brechas. Esta
 *  función queda como la última red por si alguien arma un formal por otra vía. */
export function assertFormalReadyToSubmit(overallStatus) {
    if (overallStatus !== 'ready') {
        throw AppError.validation('The analysis has unmet requirements. Resolve them before applying to the network.', 'network.gaps_open');
    }
}
/** R1 invariant (spec §10, DECISIONS 2026-08-02/04): the network publisher
 *  REFUSES anything that is not explicitly declared publishable.
 *
 *  FAIL-CLOSED on purpose: `containsPii: true` (the roster annex) is refused,
 *  and so is an ABSENT flag — an object that merely forgot to carry it, or a
 *  document rehydrated from Firestore with the field dropped, must never slip
 *  through. Only a literal `false` passes; `buildPublishablePacket` stamps it. */
export function assertNetworkPublishable(artifact) {
    if (artifact.containsPii === true) {
        throw AppError.validation('That document contains personal data and cannot be sent to the network.', 'network.contains_pii');
    }
    if (artifact.containsPii !== false) {
        throw AppError.validation('Este artefacto no se declara publicable a la red (falta containsPii: false). Se rechaza por defecto.', 'network.not_declared_publishable');
    }
}
/** A new round may be submitted only when no round is currently open
 *  (`submitted`). `returned`, `approved` and `withdrawn` are terminal for their
 *  round and do not block a new one. */
export function canSubmitToNetwork(openSubmission) {
    return openSubmission === null || openSubmission.status !== 'submitted';
}
/** Las decisiones, como dato. Existe porque el análisis asistido le DICE al
 *  modelo cuáles son las opciones: sin una lista runtime habría que escribirlas
 *  a mano en el prompt, y una quinta decisión futura no llegaría ahí — el modelo
 *  seguiría proponiendo entre cuatro sin que nada fallara. El `satisfies` obliga
 *  a que la lista y el tipo no se separen. */
export const REVIEWER_DECISIONS = [
    'approved',
    'approved_with_conditions',
    'returned',
    'rejected',
];
/** En qué ESTADO queda una ronda resuelta con esta decisión.
 *
 *  Existe porque el repositorio escribía `status: decision` directamente, y al
 *  aparecer la cuarta decisión eso metió en el documento un estado que la lista
 *  de estados no contiene: la pantalla no encontró su traducción y mostró el
 *  texto de reserva —«Revisa los datos ingresados»— en medio de una frase.
 *
 *  Una ronda aprobada con condiciones **está aprobada**. Lo condicional es una
 *  cualidad de la CERTIFICACIÓN, no un estado de la ronda; es la misma razón por
 *  la que la certificación tampoco tiene un estado `conditional`. Qué tipo de
 *  aprobación fue se conserva en `decision.decision`, que no se pierde. */
export function statusForDecision(decision) {
    return decision === 'approved_with_conditions' ? 'approved' : decision;
}
export function decisionGrants(decision) {
    return decision === 'approved' || decision === 'approved_with_conditions';
}
/** Guarda del veredicto del revisor.
 *
 *  UNA ADVISORY NO PUEDE APROBARSE. No es que aprobarla "no haga nada": es que la
 *  acción no existe. Si el botón estuviera y no moviera la escalera, el TC leería
 *  que avanzó cuando no avanzó — una señal falsa en la primera pantalla que TMAI
 *  usa. Una consulta se RESPONDE con observaciones, y eso es todo lo que ofrece.
 *  Al ser guarda y no condicional, `applyNetworkStageChange` ni siquiera es
 *  alcanzable desde una advisory.
 *
 *  Y UNA RONDA YA SUPERSEDED TAMPOCO SE DECIDE. El revisor pudo abrir el detalle
 *  ANTES de que el seminario presentara su formal —misma clase de carrera que
 *  resolvimos con el id determinístico— y estaría decidiendo sobre una ronda
 *  muerta. El mensaje tiene que explicar QUÉ pasó: que el seminario presentó una
 *  solicitud formal que reemplazó esta consulta. Un "no se puede" genérico lo
 *  dejaría pensando que es una falla del sistema. */
export function validateSubmissionDecision(
/* SOLO lo que la guarda de verdad mira. Estaba tipada a `NetworkSubmission`
   entera, y eso la ataba a la ronda de membresía aunque nunca leyera un campo
   suyo. Al aparecer la ronda CAP quedó a la vista: son las reglas que valen
   para CUALQUIER ronda —una consulta no se aprueba, devolver exige
   observaciones, una ronda cerrada no se re-decide— y por eso ahora piden lo
   mínimo que necesitan. Ensanchar el parámetro es lo que evita el camino
   fácil de copiar la función y que las dos se separen con el tiempo. */
submission, input) {
    if (submission.status === 'superseded') {
        throw AppError.conflict('This advisory was superseded by a formal application from the seminary', 'network.round_superseded');
    }
    if (!isOpenStatus(submission.status)) {
        throw AppError.validation(`Only a submission under review can be decided (current: ${submission.status})`, 'network.only_open_round_decidable');
    }
    // Una consulta no otorga NADA, y eso incluye otorgar con condiciones.
    if (decisionGrants(input.decision) && submission.submissionKind !== 'formal') {
        throw AppError.validation('An advisory submission is answered with observations, not approved', 'network.advisory_not_approvable');
    }
    if (!decisionGrants(input.decision) && input.observations.trim() === '') {
        throw AppError.validation('Returning or rejecting a submission requires observations', 'network.return_requires_observations');
    }
}
//# sourceMappingURL=network-submission.entity.js.map